import React, { useEffect } from "react";

import {
  GraduationCap,
  BookOpen,
  Target,
  Users,
  TrendUp,
  Phone,
  ArrowRight,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import {
  fadeUp,
  fadeLeft,
  fadeRight,
  staggerContainer,
  cardItem,
} from "lib/animations";
import { useInView } from "lib/hooks/useInView";

const offerings = [
  {
    title: "IOE Entrance Preparation",
    description:
      "Complete syllabus coverage for both BE and B.Arch entrance exams with expert guidance and proven strategies.",
  },
  {
    title: "Premium Study Materials",
    description:
      "High-quality notes and learning materials curated by experts, toppers, and industry professionals.",
  },
  {
    title: "Advanced Practice Sets",
    description:
      "Hundreds of chapter-wise practice questions with detailed hints, solutions, and explanations.",
  },
  {
    title: "Active Community",
    description:
      "Join thousands of learners in our Discord community for peer learning and instant doubt resolution.",
  },
];

const whyChoose = [
  "Authentic and cross-checked questions from past papers",
  "Comprehensive study materials and HD video lectures",
  "Advanced techniques and tricks for cracking exams",
  "Interactive learning environment with live sessions",
  "Detailed performance analysis and progress tracking",
  "Same-day doubt support from dedicated mentors",
];

const About = () => {
  const [storyRef, storyInView] = useInView<HTMLDivElement>();
  const [offeringsRef, offeringsInView] = useInView<HTMLDivElement>();


  return (
    <section id="about" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        {/* LEFT — Story */}
        <motion.div
          ref={storyRef}
          variants={staggerContainer}
          initial="hidden"
          animate={storyInView ? "show" : "hidden"}
          className="space-y-7"
        >
          <motion.div variants={fadeLeft}>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 leading-snug">
              Empowering Dreams,<br />Creating Engineers
            </h3>
            <p className="text-gray-600 leading-relaxed mb-4">
              Since 2078 BS, Clamphook Academy has grown into the
              best IOE entrance preparation institute in Nepal, helping over
              100,000 students compete for a seat at Pulchowk and beyond.
              Founded by IOE toppers in Kathmandu, we teach exactly what
              worked for us.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our commitment goes beyond just exam preparation: we nurture
              critical thinking, problem-solving skills, and the confidence
              needed to excel in engineering careers.
            </p>
          </motion.div>

          <motion.div variants={fadeLeft}>
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              What Makes Us Unique?
            </h4>
            <div className="space-y-3">
              {whyChoose.map((item, index) => (
                <div key={index} className="flex items-start gap-3 group">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0 group-hover:scale-150 transition-transform duration-300" />
                  <p className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors duration-200">
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-3 pt-1">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => (window.location.href = "/login")}
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary-600 text-white font-semibold rounded-xl px-6 py-3 text-sm transition-colors"
            >
              <GraduationCap className="w-4 h-4" />
              Join Our Community
              <ArrowRight className="w-4 h-4" />
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => (window.location.href = "tel:015918663")}
              className="inline-flex items-center justify-center gap-2 border border-primary-700 text-primary-700 hover:bg-primary-50 font-semibold rounded-xl px-6 py-3 text-sm transition-colors"
            >
              <Phone className="w-4 h-4" />
              Contact Us
            </motion.button>
          </motion.div>
        </motion.div>

        {/* RIGHT — Offerings */}
        <motion.div
          ref={offeringsRef}
          variants={staggerContainer}
          initial="hidden"
          animate={offeringsInView ? "show" : "hidden"}
          className="space-y-4"
        >
          <motion.div variants={fadeRight} className="mb-6">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Comprehensive Programs
            </h3>
            <p className="text-sm text-gray-500">
              Specialized programs designed to ensure your success in IOE entrance exams.
            </p>
          </motion.div>

          {offerings.map((item, index) => (
            <motion.div
              key={index}
              variants={cardItem}
              whileHover={{ x: 6, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-gray-100 shadow-sm group"
            >
              <div className="w-11 h-11 rounded-xl bg-primary-50 border border-primary-50 flex items-center justify-center flex-shrink-0">
                <span className="text-xl font-bold text-primary leading-none">
                  {index + 1}.
                </span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors duration-200">
                  {item.title}
                </h4>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {item.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;

// This file exists to be imported as a component/layout by other pages, not
// to be its own public route. Next.js Pages Router treats any file here with
// a default export as a real URL though, so without this it was silently
// indexable at its own path with broken/duplicate content. This forces a 404
// for that accidental route instead.
export async function getStaticProps() {
  return { notFound: true };
}
